import { useEffect, useState } from 'react';
import {
  can,
  faNum as fa,
  formatRial,
  MOTOR_APPLICATIONS,
  MOTOR_APPLICATION_LABELS_FA,
  type MotorApplication,
  type MotorDesignRequest,
  type MotorDesignResponse,
} from '@kia/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { downloadText } from '../lib/download';
import { Button, Card, Chip, Field, Input, Kpi, NumberInput, Textarea, useToast } from '../ui';
import { LandedCostPanel } from './LandedCostPanel';
import { HelpPanel } from './HelpPanel';

interface SavedRow {
  id: number;
  created_at: string;
  title: string;
  application: string;
  passes: boolean;
  cogs_rial: number;
}

/**
 * طراح موتور — enter the duty (power, speed, supply, start torque, ambient, sale price) and the server
 * runs the full electromagnetic + thermal + cost + PSC design and returns the cheapest motor that
 * passes every gate, or an honest reason none did. Read-level: any signed-in role may design.
 */

function Gate({ ok, label }: { ok: boolean; label: string }) {
  return (
    <Chip tone={ok ? 'success' : 'danger'}>
      {ok ? '✓' : '✗'} {label}
    </Chip>
  );
}

export function MotorDesignerScreen() {
  const toast = useToast();
  const [application, setApplication] = useState<MotorApplication>('desert_cooler');
  const [power, setPower] = useState(25);
  const [speed, setSpeed] = useState(1380);
  const [voltage, setVoltage] = useState(220);
  const [poles, setPoles] = useState(4);
  const [minV, setMinV] = useState(190);
  const [startTorque, setStartTorque] = useState(0.1);
  const [ambient, setAmbient] = useState(45);
  const [margin, setMargin] = useState(10);
  const [sale, setSale] = useState(8_000_000);
  // Lamination geometry (technical office), in mm; defaults mirror the frozen default lamination.
  const [useLam, setUseLam] = useState(false);
  const [lamBore, setLamBore] = useState(60);
  const [lamOuter, setLamOuter] = useState(90);
  const [lamSlots, setLamSlots] = useState(24);
  const [lamTooth, setLamTooth] = useState(3.5);
  const [lamYoke, setLamYoke] = useState(8);
  const [lamAirgap, setLamAirgap] = useState(0.3);
  const [lamSlotW, setLamSlotW] = useState(3);
  const [lamSlotH, setLamSlotH] = useState(10);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState<MotorDesignResponse | null>(null);
  const [prompt, setPrompt] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [research, setResearch] = useState(true);
  const [aiReply, setAiReply] = useState('');
  const [aiSources, setAiSources] = useState<{ url: string; title: string }[]>([]);
  const { user } = useAuth();
  const canSave = user ? can(user.role, 'motor.design') : false;
  const canProductize = user ? can(user.role, 'config.write') : false;
  const [productizing, setProductizing] = useState(false);
  const [title, setTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState<SavedRow[]>([]);
  function refreshHistory() {
    void api<{ designs: SavedRow[] }>('/api/motor/designs').then((r) => {
      if (r.ok) setHistory((r.body as { designs: SavedRow[] }).designs);
    });
  }
  useEffect(refreshHistory, []);

  function applyRequest(q: MotorDesignRequest) {
    setApplication(q.application);
    setPower(q.requiredShaftPowerW);
    setSpeed(q.targetSpeedRpm);
    setVoltage(q.supplyVoltage);
    setPoles(q.poles);
    setMinV(q.minStartVoltage);
    setStartTorque(q.requiredStartTorqueNm);
    setAmbient(q.ambientC);
    setMargin(q.insulationMarginK);
    setSale(q.salePriceRial);
  }

  function currentRequest() {
    return {
      application,
      requiredShaftPowerW: power,
      targetSpeedRpm: speed,
      supplyVoltage: voltage,
      poles,
      minStartVoltage: minV,
      requiredStartTorqueNm: startTorque,
      ambientC: ambient,
      insulationMarginK: margin,
      salePriceRial: sale,
      ...(useLam
        ? {
            lamination: {
              boreDiameterMm: lamBore,
              outerDiameterMm: lamOuter,
              slots: lamSlots,
              toothWidthMm: lamTooth,
              yokeDepthMm: lamYoke,
              airgapMm: lamAirgap,
              slotWidthMm: lamSlotW,
              slotHeightMm: lamSlotH,
            },
          }
        : {}),
    };
  }

  async function saveDesign() {
    if (!(power > 0) || !(sale > 0)) {
      toast('ابتدا مشخصات را کامل کنید', 'error');
      return;
    }
    setSaving(true);
    const r = await api<{ id: number }>('/api/motor/designs', {
      method: 'POST',
      body: JSON.stringify({ title, request: currentRequest() }),
    });
    setSaving(false);
    if (!r.ok) {
      toast((r.body as { message?: string }).message ?? 'خطا در ذخیره', 'error');
      return;
    }
    toast('طرح ذخیره شد ✓');
    setTitle('');
    refreshHistory();
  }

  // Productize a PASSING design → a real product (R3 slice 5). The server RE-RUNS the design authoritatively,
  // maps it onto the real BOM groups, and returns the new code; the motor then costs & sells like any product.
  async function productize() {
    const name = title.trim();
    if (!name) {
      toast('برای ثبت محصول، ابتدا یک نام در «عنوان طرح» وارد کنید', 'error');
      return;
    }
    setProductizing(true);
    const r = await api<{ code: string }>('/api/motor/productize', {
      method: 'POST',
      body: JSON.stringify({ request: currentRequest(), name }),
    });
    setProductizing(false);
    if (!r.ok) {
      toast((r.body as { message?: string }).message ?? 'خطا در ثبت محصول', 'error');
      return;
    }
    toast(`محصول ثبت شد ✓ (کد ${(r.body as { code: string }).code}) — در «محصولات و مواد» ببینید`);
    setTitle('');
  }

  async function openSaved(id: number) {
    const r = await api<{ request: MotorDesignRequest; summary: MotorDesignResponse }>(
      `/api/motor/designs/${id}`,
    );
    if (!r.ok) {
      toast('خطا در بازکردن طرح', 'error');
      return;
    }
    const rec = r.body as { request: MotorDesignRequest; summary: MotorDesignResponse };
    applyRequest(rec.request);
    setRes(rec.summary);
  }

  type AiBody = {
    request?: MotorDesignRequest;
    response?: MotorDesignResponse;
    reply?: string;
    note?: string;
    sources?: { url: string; title: string }[];
  };

  async function aiDesign() {
    const text = prompt.trim();
    if (!text) {
      toast('درخواست خود را به زبان ساده بنویسید', 'error');
      return;
    }
    setAiBusy(true);
    setAiReply('');
    setAiSources([]);
    const r = await api<AiBody>('/api/motor/ai', {
      method: 'POST',
      body: JSON.stringify({ prompt: text, research }),
    });
    setAiBusy(false);
    if (!r.ok) {
      toast((r.body as { message?: string }).message ?? 'خطا در طراحی هوشمند', 'error');
      return;
    }
    const body = r.body as AiBody;
    // A question (no design) → show the chief engineer's answer in its own panel, not an error toast.
    if (body.reply && !body.request) {
      setAiReply(body.reply);
      setAiSources(body.sources ?? []);
      return;
    }
    if (body.request) {
      const q = body.request;
      setApplication(q.application);
      setPower(q.requiredShaftPowerW);
      setSpeed(q.targetSpeedRpm);
      setVoltage(q.supplyVoltage);
      setPoles(q.poles);
      setMinV(q.minStartVoltage);
      setStartTorque(q.requiredStartTorqueNm);
      setAmbient(q.ambientC);
      setMargin(q.insulationMarginK);
      setSale(q.salePriceRial);
    }
    if (body.response) setRes(body.response);
    // The model's engineering commentary + any sources it researched travel with a design too.
    if (body.note) setAiReply(body.note);
    setAiSources(body.sources ?? []);
  }

  async function design() {
    if (!(power > 0) || !(sale > 0)) {
      toast('توان خروجی و قیمت فروش لازم است', 'error');
      return;
    }
    setBusy(true);
    const r = await api<MotorDesignResponse>('/api/motor/design', {
      method: 'POST',
      body: JSON.stringify(currentRequest()),
    });
    setBusy(false);
    if (!r.ok) {
      toast((r.body as { message?: string }).message ?? 'خطا در طراحی', 'error');
      return;
    }
    setRes(r.body as MotorDesignResponse);
  }

  const [reporting, setReporting] = useState(false);
  async function downloadReport() {
    if (!(power > 0) || !(sale > 0)) {
      toast('ابتدا مشخصات را کامل کنید', 'error');
      return;
    }
    setReporting(true);
    const r = await api<{ markdown: string }>('/api/motor/report', {
      method: 'POST',
      body: JSON.stringify(currentRequest()),
    });
    setReporting(false);
    if (!r.ok) {
      toast((r.body as { message?: string }).message ?? 'خطا در ساخت گزارش', 'error');
      return;
    }
    downloadText('kia-motor-report.md', (r.body as { markdown: string }).markdown);
  }

  return (
    <div className="stack" style={{ display: 'grid', gap: 16 }}>
      <Card title="طراحی با زبان طبیعی (مهندس ارشد هوشمند)">
        <p style={{ margin: '0 0 10px', color: 'var(--ink-2)', lineHeight: 1.9 }}>
          دستیار در نقشِ <b>مهندس ارشد طراحی موتور کیا</b> عمل می‌کند: درخواست را به مشخصات
          ساختاریافته تبدیل و همان موتورِ محاسباتیِ قطعی را اجرا می‌کند؛ همچنین می‌توانید هر پرسشی
          دربارهٔ فیزیک، مفروضات یا محدودیت‌های ابزار بپرسید و پاسخِ مهندسی بگیرید. اعداد را از
          موتور محاسباتی می‌گیرد و هیچ عددی از خود نمی‌سازد.
        </p>
        <Textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={2}
          placeholder="مثلاً: یک موتور هودِ کم‌صدا حدود ۳۰ وات برای برق ۲۳۰ ولت که در افت ولتاژ هم راه بیفتد — یا بپرسید: «فرض اشباع هسته چیست؟»"
        />
        <div
          style={{ marginTop: 8, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}
        >
          <Button onClick={() => void aiDesign()} disabled={aiBusy}>
            {aiBusy ? 'در حال کار…' : 'طراحی / پرسش هوشمند'}
          </Button>
          <label
            style={{ display: 'inline-flex', gap: 6, alignItems: 'center', color: 'var(--ink-2)' }}
          >
            <input
              type="checkbox"
              checked={research}
              onChange={(e) => setResearch(e.target.checked)}
            />
            جست‌وجوی اینترنتی (استانداردها، قیمت مواد، رقبا)
          </label>
          <span style={{ color: 'var(--ink-3)' }}>
            برای طراحی، مشخصات ساختاریافته اجرا می‌شود؛ برای پرسش، پاسخِ مهندسی داده می‌شود.
          </span>
        </div>

        {aiReply ? (
          <div
            style={{
              marginTop: 12,
              borderInlineStart: '4px solid var(--navy)',
              background: 'var(--field)',
              borderRadius: 'var(--radius-xs)',
              padding: '12px 14px',
            }}
          >
            <div style={{ fontWeight: 700, color: 'var(--navy)', marginBottom: 6 }}>
              پاسخ مهندس ارشد
            </div>
            <div
              style={{
                whiteSpace: 'pre-wrap',
                direction: 'rtl',
                textAlign: 'right',
                lineHeight: 2,
              }}
            >
              {aiReply}
            </div>
            {aiSources.length > 0 ? (
              <div style={{ marginTop: 10, borderTop: '1px solid var(--line)', paddingTop: 8 }}>
                <div style={{ color: 'var(--ink-3)', marginBottom: 4 }}>
                  منابع (جست‌وجوی اینترنتی):
                </div>
                <ol style={{ margin: 0, paddingInlineStart: 20, lineHeight: 1.9 }}>
                  {aiSources.map((s, i) => (
                    <li key={i}>
                      <a
                        href={s.url}
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: 'var(--navy)' }}
                      >
                        {s.title}
                      </a>
                    </li>
                  ))}
                </ol>
              </div>
            ) : null}
          </div>
        ) : null}
      </Card>

      <Card title="طراح موتور کیا — مشخصات درخواست">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))',
            gap: 12,
          }}
        >
          <Field label="کاربرد">
            <select
              className="input"
              value={application}
              onChange={(e) => setApplication(e.target.value as MotorApplication)}
            >
              {MOTOR_APPLICATIONS.map((a) => (
                <option key={a} value={a}>
                  {MOTOR_APPLICATION_LABELS_FA[a]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="توان خروجی (وات)">
            <NumberInput value={power} onValue={setPower} />
          </Field>
          <Field label="دور نامی (rpm)">
            <NumberInput value={speed} onValue={setSpeed} />
          </Field>
          <Field label="ولتاژ برق (V)">
            <NumberInput value={voltage} onValue={setVoltage} />
          </Field>
          <Field label="تعداد قطب">
            <NumberInput value={poles} onValue={setPoles} />
          </Field>
          <Field label="حداقل ولتاژ راه‌اندازی (V)">
            <NumberInput value={minV} onValue={setMinV} />
          </Field>
          <Field label="گشتاور راه‌اندازی لازم (N·m)">
            <NumberInput value={startTorque} onValue={setStartTorque} allowDecimal />
          </Field>
          <Field label="دمای محیط (°C)">
            <NumberInput value={ambient} onValue={setAmbient} />
          </Field>
          <Field label="حاشیهٔ حرارتی (K)">
            <NumberInput value={margin} onValue={setMargin} />
          </Field>
          <Field label="قیمت فروش (ریال)">
            <NumberInput value={sale} onValue={setSale} />
          </Field>
        </div>
        <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
          <Button onClick={() => void design()} disabled={busy}>
            {busy ? 'در حال طراحی…' : 'طراحی کن'}
          </Button>
          <Button variant="secondary" onClick={() => void downloadReport()} disabled={reporting}>
            {reporting ? 'در حال ساخت…' : 'دریافت گزارش (Markdown)'}
          </Button>
        </div>
        {canSave ? (
          <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
            <Input
              placeholder="عنوان طرح (اختیاری)"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              style={{ maxWidth: 260 }}
            />
            <Button variant="secondary" onClick={() => void saveDesign()} disabled={saving}>
              {saving ? 'در حال ذخیره…' : 'ذخیره در تاریخچه'}
            </Button>
            {res?.passes && canProductize ? (
              <Button onClick={() => void productize()} disabled={productizing}>
                {productizing ? 'در حال ثبت…' : 'ثبت به‌عنوان محصول'}
              </Button>
            ) : null}
          </div>
        ) : null}
      </Card>

      <Card title="هندسهٔ لمینیشن (دفتر فنی) — اختیاری">
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
          <input type="checkbox" checked={useLam} onChange={(e) => setUseLam(e.target.checked)} />
          استفاده از هندسهٔ سفارشیِ لمینیشن (به‌جای هندسهٔ پیش‌فرض) — ابعاد بر حسب میلی‌متر
        </label>
        {useLam ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))',
              gap: 12,
            }}
          >
            <Field label="قطر داخلی (mm)">
              <NumberInput value={lamBore} onValue={setLamBore} allowDecimal />
            </Field>
            <Field label="قطر خارجی (mm)">
              <NumberInput value={lamOuter} onValue={setLamOuter} allowDecimal />
            </Field>
            <Field label="تعداد شیار">
              <NumberInput value={lamSlots} onValue={setLamSlots} />
            </Field>
            <Field label="عرض دندانه (mm)">
              <NumberInput value={lamTooth} onValue={setLamTooth} allowDecimal />
            </Field>
            <Field label="عمق یوغ (mm)">
              <NumberInput value={lamYoke} onValue={setLamYoke} allowDecimal />
            </Field>
            <Field label="فاصلهٔ هوایی (mm)">
              <NumberInput value={lamAirgap} onValue={setLamAirgap} allowDecimal />
            </Field>
            <Field label="عرض شیار (mm)">
              <NumberInput value={lamSlotW} onValue={setLamSlotW} allowDecimal />
            </Field>
            <Field label="ارتفاع شیار (mm)">
              <NumberInput value={lamSlotH} onValue={setLamSlotH} allowDecimal />
            </Field>
          </div>
        ) : null}
      </Card>

      {res ? (
        <>
          <Card
            title={
              <span style={{ display: 'inline-flex', gap: 10, alignItems: 'center' }}>
                نتیجهٔ طراحی
                <Chip tone={res.passes ? 'success' : 'danger'}>
                  {res.passes ? 'قابل‌تولید ✓' : 'ناموفق ✗'}
                </Chip>
              </span>
            }
          >
            <div
              style={{
                borderInlineStart: `4px solid ${res.passes ? 'var(--green)' : 'var(--red)'}`,
                background: res.passes ? 'var(--green-bg)' : 'var(--red-bg)',
                borderRadius: 'var(--radius-xs)',
                padding: '10px 14px',
                margin: '0 0 14px',
                lineHeight: 1.9,
              }}
            >
              {res.verdict}
            </div>

            {res.operating ? (
              <div className="kpi-grid" style={{ marginBottom: 14 }}>
                <Kpi
                  label="توان خروجی"
                  value={`${fa(res.operating.shaftPowerW, 1)} W`}
                  tone={res.gates.meetsLoad ? 'good' : 'bad'}
                />
                <Kpi label="بازده" value={`${fa(res.operating.efficiencyPct, 1)}٪`} />
                <Kpi label="ضریب توان" value={fa(res.operating.powerFactor, 2)} />
                <Kpi label="جریان خط" value={`${fa(res.operating.lineCurrentA, 2)} A`} />
                {res.thermal ? (
                  <Kpi
                    label="دمای سیم‌پیچ"
                    value={`${fa(res.thermal.windingTempC)}°C`}
                    tone={res.gates.thermalOk ? 'good' : 'bad'}
                    hint={`کلاس ${res.thermal.insulationClass ?? '—'}`}
                  />
                ) : null}
                <Kpi
                  label="حاشیهٔ سود"
                  value={`${fa(res.margin.grossMargin * 100, 1)}٪`}
                  tone={res.gates.marginOk ? 'good' : 'bad'}
                />
                <Kpi label="بهای تمام‌شده" value={formatRial(res.cost.totalCogsRial)} />
              </div>
            ) : null}

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              <Gate ok={res.gates.meetsLoad} label="تأمین بار" />
              <Gate ok={res.gates.thermalOk} label="حرارت" />
              <Gate ok={res.gates.startsOk} label="راه‌اندازی" />
              <Gate ok={res.gates.marginOk} label="حاشیهٔ سود" />
              <Gate ok={res.gates.windableOk} label="قابلیت سیم‌پیچی" />
              <Gate ok={res.gates.saturationOk} label="عدم اشباع هسته" />
            </div>
            {res.pricesArePlaceholder ? (
              <p style={{ marginTop: 12, color: 'var(--amber)' }}>
                ⚠ قیمت‌ها آزمایشی‌اند؛ برای بهای واقعی، قیمت‌های هفتگی گروه‌های مواد را وارد کنید.
              </p>
            ) : null}
          </Card>

          {res.levers && res.operating ? (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))',
                gap: 16,
              }}
            >
              <Card title="طرح انتخاب‌شده">
                <dl className="kv">
                  <Row k="دور سیم‌پیچ اصلی" v={fa(res.levers.seriesTurns)} />
                  <Row k="طول هسته" v={`${fa(res.levers.stackLengthMm, 1)} mm`} />
                  <Row k="سطح مقطع سیم" v={`${fa(res.levers.wireAreaMm2, 3)} mm²`} />
                  <Row k="خازن" v={`${fa(res.levers.capacitorUF)} µF`} />
                  <Row k="گشتاور راه‌اندازی @ ولتاژ پایین" v={`${fa(res.startTorqueNm, 2)} N·m`} />
                </dl>
              </Card>

              <Card title="عملکرد در نقطهٔ کار">
                <dl className="kv">
                  <Row k="دور" v={`${fa(res.operating.speedRpm)} rpm`} />
                  <Row k="توان خروجی" v={`${fa(res.operating.shaftPowerW, 1)} W`} />
                  <Row k="بازده" v={`${fa(res.operating.efficiencyPct, 1)}٪`} />
                  <Row k="ضریب توان" v={fa(res.operating.powerFactor, 2)} />
                  <Row k="جریان خط" v={`${fa(res.operating.lineCurrentA, 2)} A`} />
                  {res.thermal ? (
                    <Row
                      k="دمای سیم‌پیچ / کلاس"
                      v={`${fa(res.thermal.windingTempC)}°C / ${res.thermal.insulationClass ?? '—'}`}
                    />
                  ) : null}
                </dl>
              </Card>
            </div>
          ) : null}

          <Card title="بهای تمام‌شده (COGS)">
            <table className="table">
              <thead>
                <tr>
                  <th>قلم</th>
                  <th>مقدار</th>
                  <th>بهای واحد</th>
                  <th>بها</th>
                </tr>
              </thead>
              <tbody>
                {res.cost.lines.map((l, i) => (
                  <tr key={i}>
                    <td>{l.label}</td>
                    <td>
                      {fa(l.quantity, l.unit === 'kg' ? 3 : 0)} {l.unit === 'kg' ? 'kg' : 'عدد'}
                    </td>
                    <td>{formatRial(l.unitPriceRial)}</td>
                    <td>{formatRial(l.costRial)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3}>مواد</td>
                  <td>{formatRial(res.cost.materialCostRial)}</td>
                </tr>
                <tr>
                  <td colSpan={3}>حمل ورودی</td>
                  <td>{formatRial(res.cost.freightRial)}</td>
                </tr>
                <tr>
                  <td colSpan={3}>دستمزد و سربار</td>
                  <td>{formatRial(res.cost.conversionRial)}</td>
                </tr>
                <tr>
                  <td colSpan={3}>
                    <b>جمع کل</b>
                  </td>
                  <td>
                    <b>{formatRial(res.cost.totalCogsRial)}</b>
                  </td>
                </tr>
              </tfoot>
            </table>
            <p style={{ marginTop: 8, color: 'var(--ink-3)' }}>
              حاشیهٔ سود: {fa(res.margin.grossMargin * 100, 1)}٪ · جست‌وجو:{' '}
              {fa(res.search.evaluated)} نامزد، {fa(res.search.feasible)} قابل‌قبول
              {res.search.failures.thermal > 0
                ? ` · ${fa(res.search.failures.thermal)} ردِ حرارتی`
                : ''}
            </p>
          </Card>

          {res.review ? (
            <Card
              title={
                <span style={{ display: 'inline-flex', gap: 10, alignItems: 'center' }}>
                  بازبینی مستقل (Red-team)
                  <Chip
                    tone={
                      res.review.verdict === 'blocked'
                        ? 'danger'
                        : res.review.verdict === 'caution'
                          ? 'warn'
                          : 'success'
                    }
                  >
                    {res.review.verdict === 'blocked'
                      ? '⛔ مسدود'
                      : res.review.verdict === 'caution'
                        ? '⚠ با احتیاط'
                        : '✅ پاک'}
                  </Chip>
                </span>
              }
            >
              <table className="table">
                <thead>
                  <tr>
                    <th>بررسی</th>
                    <th>وضعیت</th>
                    <th>یافته</th>
                  </tr>
                </thead>
                <tbody>
                  {res.review.findings.map((f) => (
                    <tr key={f.id}>
                      <td>{f.title}</td>
                      <td>
                        {f.pass
                          ? '✅'
                          : f.severity === 'critical'
                            ? '⛔'
                            : f.severity === 'warning'
                              ? '⚠'
                              : 'ℹ'}
                      </td>
                      <td style={{ color: 'var(--ink-3)' }}>{f.detail}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          ) : null}
        </>
      ) : null}

      {history.length > 0 ? (
        <Card title="تاریخچهٔ طرح‌ها">
          <table className="table">
            <thead>
              <tr>
                <th>عنوان</th>
                <th>کاربرد</th>
                <th>وضعیت</th>
                <th>COGS</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.id}>
                  <td>{h.title || `#${h.id}`}</td>
                  <td>
                    {MOTOR_APPLICATION_LABELS_FA[h.application as MotorApplication] ??
                      h.application}
                  </td>
                  <td>
                    <Chip tone={h.passes ? 'success' : 'danger'}>
                      {h.passes ? 'قابل‌تولید' : 'ناموفق'}
                    </Chip>
                  </td>
                  <td>{formatRial(h.cogs_rial)}</td>
                  <td>
                    <Button variant="secondary" onClick={() => void openSaved(h.id)}>
                      بازکردن
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ) : null}

      <LandedCostPanel />
      <HelpPanel />
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', gap: 12 }}>
      <span style={{ color: 'var(--ink-3)' }}>{k}</span>
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{v}</span>
    </div>
  );
}
